import axios, { AxiosError } from 'axios';
import camelCaseKeys from 'camelcase-keys';
import { enqueueSnackbar } from 'notistack';
import snakeCaseKeys from 'snakecase-keys';

import { serializeQueryParams } from './utils';

import { getAuthSession } from '@/features/auth/common';
import {
  ACCESS_DENIED_RELOAD_DELAY_MS,
  clearAccessDeniedReloadGuard,
  hasReloadedForAccessDenied,
  markReloadedForAccessDenied,
} from '@/shared/utils/accessDeniedReload';
import { isIframeWindow } from '@/shared/utils/utils';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retried?: boolean;
  }
}

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: { serialize: serializeQueryParams },
});

axiosClient.interceptors.request.use(async (config) => {
  const session = await getAuthSession();
  const idToken = session?.id_token;
  const tokenType = session?.token_type;

  if (idToken && tokenType) {
    config.headers.Authorization = `${tokenType} ${idToken}`;
  }

  if (config.headers['Content-Type'] === 'multipart/form-data') {
    return config;
  }

  if (config.params) {
    config.params = snakeCaseKeys(config.params) as unknown;
  }

  if (config.data) {
    config.data = snakeCaseKeys(config.data) as unknown;
  }

  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    // A successful response means the app is healthy again — re-arm the
    // access-denied reload guard so a future 403 can trigger a fresh reload.
    clearAccessDeniedReloadGuard();

    if (
      response.data &&
      response.headers['content-type'] === 'application/json'
    ) {
      response.data = camelCaseKeys(response.data, {
        deep: true,
        preserveConsecutiveUppercase: true,
        exclude: ['L', 'M', 'H'] as const,
      }) as unknown;
    }

    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 403) {
      const data = error.response.data;
      const message =
        typeof data === 'object' && data !== null && 'detail' in data
          ? String(data.detail)
          : 'Access denied.';

      enqueueSnackbar(message, { variant: 'warning' });

      // Recover by reloading once: the backend self-corrects the inaccessible
      // selection on the next bootstrap. The guard prevents an infinite reload
      // loop if it ever doesn't; iframes are left untouched.
      if (!isIframeWindow() && !hasReloadedForAccessDenied()) {
        markReloadedForAccessDenied();
        setTimeout(() => {
          window.location.reload();
        }, ACCESS_DENIED_RELOAD_DELAY_MS);
      }

      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      if (!isIframeWindow() && error.config && !error.config._retried) {
        error.config._retried = true;

        const session = await getAuthSession(true);

        if (session?.id_token) {
          error.config.headers.Authorization = `${session.token_type} ${session.id_token}`;
          return axiosClient(error.config);
        }
      }

      window.location.href = '/auth/login';
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
